import { Core } from "@faire/design-tokens";
import { ChevronRight } from "@faire/web/slate/icons/Chevron";
import { CartEmpty } from "@faire/web/slate/icons/foundation/CartEmpty";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

interface BrandBagButtonProps {
  showSidecart?: boolean;
  totalInCart?: number;
  setCart?: () => void;
  isBrandPage?: boolean;
}

export const BrandBagButton: React.FC<BrandBagButtonProps> = ({
  showSidecart,
  totalInCart,
  setCart,
  isBrandPage,
}) => {
  const { isTabletAndBelow } = useViewport();

  if (isTabletAndBelow) {
    return null;
  }
  if (showSidecart) {
    return (
      <BagButton onClick={setCart} $applyGreyBorder={isBrandPage}>
        <Typography>
          <StrictLocalMsg
            defaultMessage="Hide brand cart"
            description={{
              text: "Button to hide the brand side cart on the brand page",
              img: "https://cdn.faire.com/fastly/817c4941dafc497b2c5ae002f89968f076514e526a043d3931921a0319c826b4.png",
            }}
          />
        </Typography>
        <Spacer size="1x" />
        <ChevronRight fontSize="12px " />
      </BagButton>
    );
  }
  if (totalInCart && totalInCart > 0) {
    return (
      <BagButton onClick={setCart} $applyGreyBorder={isBrandPage}>
        <IconContainer>
          <InvertedCart fontSize="16px" strokeWidth={1} />
        </IconContainer>
        <Spacer size="1x" />
        <Typography>
          <StrictLocalMsg
            defaultMessage="{totalInCartNumber, plural, one {# product} other {# products}}"
            values={{
              totalInCartNumber: totalInCart,
            }}
            description={{
              text: "Button text showing number of products in the brand side cart",
              img: "https://cdn.faire.com/fastly/5ab0f98ebbe9b9686c03604c6bcbd05dc66ccb451fa7cbef8cfb01b54fc4cad9.png",
            }}
          />
        </Typography>
      </BagButton>
    );
  }
  return (
    <BagButton
      onClick={setCart}
      data-test-id="brand-bag-toggle"
      $applyGreyBorder={isBrandPage}
    >
      <InvertedCart fontSize="16px" strokeWidth={1} />
    </BagButton>
  );
};

const IconContainer = styled.div`
  height: 32px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const BagButton = styled(Row).attrs({
  justify: "center",
  align: "center",
})<{ $applyGreyBorder?: boolean }>`
  height: ${getSpacing("5x")};
  padding: ${getSpacing(10, "2x", 10, "2x")};
  margin-left: ${getSpacing("2x")};
  border-radius: 100px;
  border: 1px solid
    ${(props) =>
      props.$applyGreyBorder ? Core.border.muted : Core.border.default};
  &:hover {
    cursor: pointer;
    background-color: ${Core.surface.muted};
  }
`;

const InvertedCart = styled(CartEmpty)`
  transform: scaleX(-1);
`;
